import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wifi, Phone, MapPin, Clock, Megaphone, ConciergeBell, Compass, CalendarClock, UtensilsCrossed,
  Loader2, ChevronRight, ChevronLeft, User, LogIn, CheckCircle2, Bell, BellRing, MapPinned, UserCircle,
  MessageCircle, Send, Plus, Minus, ShoppingBag, ShieldAlert, LayoutGrid, Receipt, KeyRound, LogOut, Star, Sparkles, Gift, Download, Armchair, ClipboardCheck, SprayCan, ExternalLink,
} from "lucide-react";
import axios from "axios";
import { API, fileUrl } from "@/lib/api";
import { enablePush, pushSupported } from "@/lib/push";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { InstallAppButton } from "@/components/InstallAppButton";
import { TableReservationDialog, CheckinDialog, HousekeepingDialog } from "@/components/hub/VenueDialogs";
import { venueTerms } from "@/lib/venues";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const statusColor = {
  pending: "bg-amber-100 text-amber-800", placed: "bg-amber-100 text-amber-800", open: "bg-amber-100 text-amber-800", requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800", preparing: "bg-blue-100 text-blue-800", in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800", delivered: "bg-green-100 text-green-800", resolved: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700", declined: "bg-red-100 text-red-700",
};

const guestToken = (slug) => localStorage.getItem(`grh_guest_token_${slug}`);
const authHeader = (slug) => { const t = guestToken(slug); return t ? { headers: { Authorization: `Bearer ${t}` } } : {}; };

export default function GuestHub() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState(() => (params.get("table") ? "dine" : "home"));
  const tableNo = params.get("table");
  const [venueDialog, setVenueDialog] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(() => params.get("review") === "1");
  const [detail, setDetail] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushOn, setPushOn] = useState(() => localStorage.getItem(`grh_push_${slug}`) === "1");
  const [cart, setCart] = useState([]);
  const [guest, setGuest] = useState(() => JSON.parse(localStorage.getItem(`grh_guest_${slug}`) || "null"));
  const setpwToken = params.get("setpw");

  const reload = () => axios.get(`${API}/public/hub/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  useEffect(() => { reload(); }, [slug]);

  useEffect(() => {
    const b = data?.business;
    if (!b) return;
    document.title = b.seo_title || `${b.name} · Guest Hub`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = b.seo_description || b.description || b.welcome_message || "";
    const manifest = document.getElementById("app-manifest");
    const touch = document.getElementById("app-touch-icon");
    const title = document.getElementById("app-title");
    const theme = document.querySelector('meta[name="theme-color"]');
    const prev = [manifest?.href, touch?.href, title?.content, theme?.content];
    if (manifest) manifest.href = `${API}/public/hub/${slug}/manifest.json`;
    if (touch && b.logo_url) touch.href = fileUrl(b.logo_url);
    if (title) title.content = b.name;
    if (theme) theme.content = b.accent_color || "#C5A880";
    return () => {
      if (manifest) manifest.href = prev[0];
      if (touch) touch.href = prev[1];
      if (title) title.content = prev[2];
      if (theme) theme.content = prev[3];
    };
  }, [data, slug]);

  const turnOnPush = async () => {
    setPushing(true);
    try {
      await enablePush(slug, guest?.id);
      localStorage.setItem(`grh_push_${slug}`, "1");
      setPushOn(true);
      toast.success("Notifications enabled");
    } catch (e) { toast.error(e.message || "Could not enable notifications"); }
    finally { setPushing(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-background text-center p-6">
      <div><Compass className="w-10 h-10 text-accent mx-auto mb-4" /><h1 className="text-2xl font-serif">Hub not found</h1><p className="text-muted-foreground text-sm mt-2">This guest relations hub doesn't exist.</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>;

  const { business: b, directory, services, announcements, schedule = [], nearby = [], menu = [], upgrades = [] } = data;
  const accent = b.accent_color || "#C5A880";
  const f = data.features || {};
  const aiOn = b.ai_concierge_enabled !== false && !!f.ai_concierge;
  const chatOn = aiOn || !!f.messages;
  const terms = venueTerms(b.venue_type);
  const tableMode = !!(tableNo && f.table_ordering);
  const canOrder = tableMode || !!f.menu;
  const quickActions = [
    { id: "table", on: f.table_reservations, icon: Armchair, title: "Book a table", sub: "Reserve in seconds" },
    { id: "checkin", on: f.online_checkin, icon: ClipboardCheck, title: "Check in online", sub: "Skip the queue at reception" },
    { id: "housekeeping", on: f.housekeeping, icon: SprayCan, title: "Housekeeping", sub: "Towels, cleaning & more" },
  ].filter((q) => q.on);
  const tabs = [
    { id: "home", icon: Compass, label: "Home", on: true },
    { id: "explore", icon: LayoutGrid, label: "Explore", on: f.directory || f.nearby_map },
    { id: "dine", icon: UtensilsCrossed, label: terms.dine, on: f.menu || f.table_ordering },
    { id: "schedule", icon: CalendarClock, label: "Schedule", on: f.schedule },
    { id: "more", icon: ConciergeBell, label: "More", on: true },
  ].filter((t) => t.on);

  const bookSlot = (ev) => setSelectedService({ id: ev.id, schedule_event_id: ev.id, name: `${ev.title} — ${ev.day} ${ev.start_time || ""}`.trim(), category: ev.category, price: null });

  const addToCart = (m) => setCart((c) => { const e = c.find((i) => i.menu_item_id === m.id); return e ? c.map((i) => i.menu_item_id === m.id ? { ...i, qty: i.qty + 1 } : i) : [...c, { menu_item_id: m.id, name: m.name, price: m.price, qty: 1 }]; });
  const decCart = (id) => setCart((c) => c.flatMap((i) => i.menu_item_id === id ? (i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : []) : [i]));
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const placeOrder = async () => {
    if (tableMode) {
      try {
        await axios.post(`${API}/public/hub/${slug}/table-orders`, { items: cart, table: tableNo, name: guest?.name || "", notes: "" });
        setCart([]);
        toast.success(`Order sent to the kitchen — Table ${tableNo}`);
      } catch (e) { toast.error(e.response?.data?.detail || "Could not place order"); }
      return;
    }
    if (!guest) { toast.error("Please sign in to order to your room"); setAuthOpen(true); return; }
    try {
      await axios.post(`${API}/public/hub/${slug}/room-orders`, { items: cart, room: guest.room || "", notes: "" }, authHeader(slug));
      setCart([]);
      toast.success("Order placed — billed to your room");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not place order"); }
  };

  const requestUpgrade = async (u) => {
    if (!guest) { toast.error("Please sign in to request an upgrade"); setAuthOpen(true); return; }
    try {
      await axios.post(`${API}/public/hub/${slug}/upgrade-requests`, { upgrade_id: u.id }, authHeader(slug));
      toast.success("Upgrade requested — reception will confirm shortly");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not request upgrade"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-md mx-auto bg-background min-h-screen shadow-2xl relative pb-24 overflow-hidden">
        {/* Cover */}
        <div className="relative h-56">
          <img src={fileUrl(b.cover_image_url) || "https://images.unsplash.com/photo-1775257796019-3e8db981a1a6"} alt={b.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute top-4 right-4 flex gap-2">
            {pushSupported() && (
              <button onClick={turnOnPush} disabled={pushing || pushOn} data-testid="enable-push-btn" className="bg-white/20 backdrop-blur-md rounded-full p-2.5 text-white disabled:opacity-70">
                {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : pushOn ? <BellRing className="w-4 h-4" style={{ color: accent }} /> : <Bell className="w-4 h-4" />}
              </button>
            )}
            <button onClick={() => setAuthOpen(true)} data-testid="guest-account-btn" className="bg-white/20 backdrop-blur-md rounded-full p-2.5 text-white">
              {guest ? <CheckCircle2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
            </button>
          </div>
          <div className="absolute bottom-5 left-5 right-5 text-white">
            <p className="uppercase tracking-[0.2em] text-[10px] mb-2" style={{ color: accent }}>{b.business_type}</p>
            <h1 className="text-3xl font-serif leading-tight">{b.name}</h1>
            <p className="text-white/80 text-sm mt-1">{b.tagline}</p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="px-5 py-6">
            {tab === "home" && (
              <div className="space-y-6" data-testid="hub-home">
                <p className="text-foreground font-serif text-xl">{b.welcome_message}</p>
                {b.description && <p className="text-muted-foreground text-sm leading-relaxed">{b.description}</p>}

                {quickActions.length > 0 && (
                  <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${quickActions.length}, minmax(0, 1fr))` }} data-testid="hub-quick-actions">
                    {quickActions.map((q) => (
                      <button key={q.id} onClick={() => setVenueDialog(q.id)} data-testid={`hub-action-${q.id}`} className="rounded-md border border-border bg-card p-3 text-left hover-lift">
                        <q.icon className="w-5 h-5 mb-2" style={{ color: accent }} strokeWidth={1.5} />
                        <p className="text-sm font-medium text-foreground leading-tight">{q.title}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{q.sub}</p>
                      </button>
                    ))}
                  </div>
                )}

                {guest && (guest.check_in || guest.check_out) && (
                  <div className="rounded-md border border-border p-4 bg-card" data-testid="my-stay-card">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Your stay</p>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Check-in</span><span className="text-foreground font-medium">{guest.check_in || "—"}</span></div>
                    <div className="flex justify-between text-sm mt-1"><span className="text-muted-foreground">Check-out</span><span className="text-foreground font-medium">{guest.check_out || "—"}</span></div>
                    {guest.room && <div className="flex justify-between text-sm mt-1"><span className="text-muted-foreground">Room</span><span className="text-foreground font-medium">{guest.room}</span></div>}
                  </div>
                )}

                {pushSupported() && !pushOn && (
                  <button onClick={turnOnPush} disabled={pushing} data-testid="home-enable-push" className="w-full rounded-md p-4 flex items-center gap-3 text-left" style={{ backgroundColor: `${accent}14`, border: `1px solid ${accent}55` }}>
                    <Bell className="w-5 h-5 shrink-0" style={{ color: accent }} />
                    <div className="flex-1"><p className="text-sm font-medium text-foreground">Get live updates</p><p className="text-xs text-muted-foreground">Enable notifications for dining, events & offers.</p></div>
                    {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>
                )}

                {announcements.length > 0 && (
                  <div className="rounded-md p-4" style={{ backgroundColor: `${accent}1a`, border: `1px solid ${accent}55` }} data-testid="hub-latest-announcement">
                    <div className="flex items-center gap-2 mb-1"><Megaphone className="w-4 h-4" style={{ color: accent }} /><span className="text-xs uppercase tracking-widest" style={{ color: accent }}>Latest update</span></div>
                    <p className="font-medium text-foreground text-sm">{announcements[0].title}</p>
                    <p className="text-muted-foreground text-sm mt-0.5">{announcements[0].message}</p>
                  </div>
                )}

                <InstallAppButton testId="hub-install-app" className="w-full rounded-md p-4 flex items-center gap-3 text-left bg-card border border-border hover-lift">
                  <Download className="w-5 h-5 shrink-0" style={{ color: accent }} />
                  <div className="flex-1"><p className="text-sm font-medium text-foreground">Add {b.name} to your home screen</p><p className="text-xs text-muted-foreground">Open your guest hub like an app — even with patchy Wi-Fi.</p></div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </InstallAppButton>

                <div className="grid grid-cols-2 gap-3">
                  {b.wifi_name && <InfoTile icon={Wifi} label="Wi-Fi" value={b.wifi_name} sub={b.wifi_password} accent={accent} />}
                  {b.checkout_time && <InfoTile icon={Clock} label="Check-out" value={b.checkout_time} accent={accent} />}
                  {b.contact_phone && <InfoTile icon={Phone} label="Reception" value={b.contact_phone} accent={accent} />}
                  {b.address && <InfoTile icon={MapPin} label="Location" value={b.address} accent={accent} />}
                </div>

                {upgrades.length > 0 && (
                  <div data-testid="hub-upgrades">
                    <div className="flex items-center gap-2 mb-3">
                      <Gift className="w-5 h-5" style={{ color: accent }} />
                      <h2 className="font-serif text-2xl">Enhance your stay</h2>
                    </div>
                    <div className="space-y-3">
                      {upgrades.map((u) => (
                        <div key={u.id} className="rounded-md border border-border p-4 bg-card flex items-center justify-between gap-3" data-testid={`hub-upgrade-${u.id}`}>
                          <div className="flex-1">
                            <p className="font-medium text-foreground">{u.title}</p>
                            {u.description && <p className="text-sm text-muted-foreground mt-0.5">{u.description}</p>}
                            <p className="font-serif mt-1" style={{ color: accent }}>£{Number(u.fee).toFixed(2)}</p>
                          </div>
                          <Button size="sm" onClick={() => requestUpgrade(u)} data-testid={`request-upgrade-${u.id}`} className="text-black shrink-0" style={{ backgroundColor: accent }}>Request</Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === "explore" && (
              <div className="space-y-8" data-testid="hub-explore">
                {f.directory && <div>
                  <h2 className="font-serif text-2xl mb-3">Your Stay Guide</h2>
                  {directory.length === 0 ? <Empty text="No guides added yet." /> : (
                    <div className="space-y-3">{directory.map((it) => <ExploreCard key={it.id} item={it} accent={accent} onOpen={() => setDetail({ ...it, kind: "directory" })} testid={`hub-directory-${it.id}`} />)}</div>
                  )}
                </div>}
                {f.nearby_map && <div>
                  <h2 className="font-serif text-2xl mb-3">Explore Nearby</h2>
                  {nearby.some((n) => n.lat != null) && (
                    <div className="mb-3"><MapView hotel={{ lat: b.lat, lng: b.lng, name: b.name }} markers={nearby} accent={accent} /></div>
                  )}
                  {nearby.length === 0 ? <Empty text="No nearby places yet." /> : (
                    <div className="space-y-3">{nearby.map((it) => <ExploreCard key={it.id} item={it} accent={accent} onOpen={() => setDetail({ ...it, kind: "nearby", hotel: { lat: b.lat, lng: b.lng } })} testid={`hub-nearby-${it.id}`} />)}</div>
                  )}
                </div>}
              </div>
            )}

            {tab === "dine" && (
              <div className="space-y-6" data-testid="hub-dine">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl">{tableMode || b.venue_type === "restaurant" ? "Our Menu" : "Room Service"}</h2>
                  {canOrder && <Badge variant="secondary" className="text-xs" data-testid="dine-mode-badge">{tableMode ? `Table ${tableNo}` : "Billed to room"}</Badge>}
                </div>
                {!canOrder && f.table_ordering && <p className="text-sm text-muted-foreground" data-testid="scan-table-hint">Scan the QR code on your table to order.</p>}
                {menu.length === 0 ? <Empty text="Room service menu coming soon." /> : (
                  [...new Set(menu.map((m) => m.category))].map((cat) => (
                    <div key={cat}>
                      <h3 className="font-serif text-lg mb-2" style={{ color: accent }}>{cat}</h3>
                      <div className="space-y-3">
                        {menu.filter((m) => m.category === cat).map((m) => {
                          const inCart = cart.find((i) => i.menu_item_id === m.id);
                          return (
                            <div key={m.id} className="rounded-md border border-border p-4 bg-card flex items-center justify-between gap-3" data-testid={`hub-menu-${m.id}`}>
                              <div className="flex-1">
                                <p className="font-medium text-foreground">{m.name}</p>
                                {m.description && <p className="text-sm text-muted-foreground mt-0.5">{m.description}</p>}
                                <p className="font-serif mt-1" style={{ color: accent }}>£{m.price}</p>
                              </div>
                              {!canOrder ? null : inCart ? (
                                <div className="flex items-center gap-2">
                                  <button onClick={() => decCart(m.id)} data-testid={`dec-${m.id}`} className="w-8 h-8 rounded-full border border-border flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                                  <span className="w-5 text-center text-sm font-medium">{inCart.qty}</span>
                                  <button onClick={() => addToCart(m)} data-testid={`inc-${m.id}`} className="w-8 h-8 rounded-full text-black flex items-center justify-center" style={{ backgroundColor: accent }}><Plus className="w-4 h-4" /></button>
                                </div>
                              ) : (
                                <Button size="sm" onClick={() => addToCart(m)} data-testid={`add-${m.id}`} className="text-black" style={{ backgroundColor: accent }}><Plus className="w-4 h-4" /></Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {tab === "schedule" && (
              <div className="space-y-6" data-testid="hub-schedule">
                <h2 className="font-serif text-2xl">Schedule</h2>
                {schedule.length === 0 ? <Empty text="No scheduled classes or slots yet." /> : DAYS.map((day) => {
                  const evs = schedule.filter((e) => e.day === day).sort((a, c) => (a.start_time || "").localeCompare(c.start_time || ""));
                  if (!evs.length) return null;
                  return (
                    <div key={day}>
                      <h3 className="font-serif text-lg mb-2" style={{ color: accent }}>{day}</h3>
                      <div className="space-y-3">
                        {evs.map((e) => (
                          <div key={e.id} className="rounded-md border border-border p-4 bg-card" data-testid={`hub-schedule-${e.id}`}>
                            <div className="flex items-center gap-2">
                              <span className="font-serif text-foreground">{e.start_time}{e.end_time ? `–${e.end_time}` : ""}</span>
                              <Badge variant="secondary" className="text-xs">{e.category}</Badge>
                            </div>
                            <h4 className="font-medium text-foreground mt-1">{e.title}</h4>
                            {e.description && <p className="text-sm text-muted-foreground mt-0.5">{e.description}</p>}
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                              {e.instructor && <span className="flex items-center gap-1"><UserCircle className="w-3 h-3" />{e.instructor}</span>}
                              {e.location && <span className="flex items-center gap-1"><MapPinned className="w-3 h-3" />{e.location}</span>}
                            </p>
                            <div className="flex items-center justify-between mt-3">
                              {e.remaining != null ? (
                                <span className="text-xs font-medium" style={{ color: e.remaining > 0 ? accent : "#b91c1c" }} data-testid={`hub-slot-remaining-${e.id}`}>{e.remaining > 0 ? `${e.remaining} of ${e.capacity} left` : "Fully booked"}</span>
                              ) : <span />}
                              {f.bookings && <Button size="sm" disabled={e.remaining != null && e.remaining <= 0} onClick={() => bookSlot(e)} data-testid={`hub-book-slot-${e.id}`} className="text-black disabled:opacity-50" style={{ backgroundColor: accent }}>{e.remaining != null && e.remaining <= 0 ? "Full" : "Book this slot"}</Button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab === "more" && (
              <div className="space-y-8" data-testid="hub-more">
                {f.services && f.bookings && <div>
                  <h2 className="font-serif text-2xl mb-3">Concierge Services</h2>
                  {services.length === 0 ? <Empty text="No services available." /> : (
                    <div className="space-y-3">{services.map((s) => (
                      <button key={s.id} onClick={() => setSelectedService(s)} data-testid={`hub-service-${s.id}`} className="w-full text-left rounded-md border border-border p-4 flex items-center justify-between hover-lift bg-card">
                        <div><span className="text-[10px] uppercase tracking-widest" style={{ color: accent }}>{s.category}</span><h3 className="font-medium text-foreground">{s.name}</h3><p className="text-sm text-muted-foreground mt-0.5">{s.description}</p></div>
                        <div className="text-right shrink-0 ml-3">{s.price != null && <p className="font-serif text-lg">£{s.price}</p>}<ChevronRight className="w-4 h-4 text-muted-foreground ml-auto mt-1" /></div>
                      </button>
                    ))}</div>
                  )}
                </div>}

                {f.complaints && <button onClick={() => setComplaintOpen(true)} data-testid="report-complaint-btn" className="w-full rounded-md border border-border p-4 flex items-center gap-3 bg-card hover-lift text-left">
                  <ShieldAlert className="w-5 h-5" style={{ color: accent }} />
                  <div className="flex-1"><p className="font-medium text-foreground">Report an issue</p><p className="text-xs text-muted-foreground">Let us know about anything in your room.</p></div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>}

                {f.announcements && <div>
                  <h2 className="font-serif text-2xl mb-3">Announcements</h2>
                  {announcements.length === 0 ? <Empty text="No announcements yet." /> : (
                    <div className="space-y-3">{announcements.map((a) => (
                      <div key={a.id} className="rounded-md border border-border p-4" data-testid={`hub-announcement-${a.id}`}>
                        <p className="font-medium text-foreground">{a.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{a.message}</p>
                        <p className="text-xs text-muted-foreground mt-2">{new Date(a.created_at).toLocaleString()}</p>
                      </div>
                    ))}</div>
                  )}
                </div>}

                <button onClick={() => setAuthOpen(true)} data-testid="more-account-btn" className="w-full rounded-md border border-border p-4 flex items-center gap-3 bg-card hover-lift text-left">
                  <UserCircle className="w-5 h-5" style={{ color: accent }} />
                  <div className="flex-1"><p className="font-medium text-foreground">{guest ? "My stay & requests" : "Sign in to manage your stay"}</p><p className="text-xs text-muted-foreground">Bookings, room service & folio.</p></div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Dine cart bar */}
        {tab === "dine" && cart.length > 0 && (
          <div className="fixed bottom-20 inset-x-0 max-w-md mx-auto px-3 z-40" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="cart-bar">
            <button onClick={placeOrder} className="w-full rounded-full shadow-xl px-5 py-3 text-black flex items-center justify-between" style={{ backgroundColor: accent }}>
              <span className="flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> {cartCount} item{cartCount > 1 ? "s" : ""}</span>
              <span className="font-medium">Order · £{cartTotal.toFixed(2)}</span>
            </button>
          </div>
        )}

        {/* Ask Concierge floating button */}
        {tab !== "dine" && chatOn && (
          <button onClick={() => setChatOpen(true)} data-testid="ask-concierge-btn" className="absolute bottom-24 right-5 z-40 rounded-full shadow-xl p-4 text-black hover-lift" style={{ backgroundColor: accent }}>
            <MessageCircle className="w-5 h-5" />
          </button>
        )}

        {/* Bottom nav */}
        <nav className="fixed bottom-0 inset-x-0 max-w-md mx-auto bg-ink text-cream border-t border-ink flex">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hub-tab-${t.id}`} className="flex-1 flex flex-col items-center py-3 gap-1 transition-colors" style={{ color: tab === t.id ? accent : "#A1A7B0" }}>
              <t.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[9px]">{t.label}</span>
            </button>
          ))}
        </nav>

        {/* Detail overlay */}
        <AnimatePresence>
          {detail && <DetailView item={detail} accent={accent} onClose={() => setDetail(null)} />}
        </AnimatePresence>
      </div>

      <TableReservationDialog open={venueDialog === "table"} onClose={() => setVenueDialog(null)} slug={slug} guest={guest} accent={accent} />
      <CheckinDialog open={venueDialog === "checkin"} onClose={() => setVenueDialog(null)} slug={slug} guest={guest} accent={accent} />
      <HousekeepingDialog open={venueDialog === "housekeeping"} onClose={() => setVenueDialog(null)} slug={slug} guest={guest} accent={accent} />
      <Dialog open={reviewOpen} onOpenChange={(o) => { if (!o) { setReviewOpen(false); const p = new URLSearchParams(params); p.delete("review"); setParams(p, { replace: true }); } }}>
        <DialogContent className="max-w-md" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">How was your {terms.stay}?</DialogTitle></DialogHeader>
          <FeedbackCard slug={slug} accent={accent} />
        </DialogContent>
      </Dialog>
      <ServiceRequestDialog service={selectedService} onClose={() => setSelectedService(null)} slug={slug} guest={guest} accent={accent} />
      <GuestAuthDialog open={authOpen} onClose={() => setAuthOpen(false)} slug={slug} guest={guest} setGuest={setGuest} accent={accent} />
      <ConciergeChat open={chatOpen} onClose={() => setChatOpen(false)} slug={slug} guest={guest} accent={accent} aiEnabled={aiOn} staffEnabled={!!f.messages} />
      <ComplaintDialog open={complaintOpen} onClose={() => setComplaintOpen(false)} slug={slug} guest={guest} accent={accent} />
      <SetPasswordDialog token={setpwToken} slug={slug} accent={accent} setGuest={setGuest}
        onDone={() => { const p = new URLSearchParams(params); p.delete("setpw"); setParams(p, { replace: true }); }} />
    </div>
  );
}

function InfoTile({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-md border border-border p-3 bg-card">
      <Icon className="w-4 h-4 mb-2" style={{ color: accent }} strokeWidth={1.5} />
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground truncate">{value}</p>
      {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-12 text-sm text-muted-foreground border border-dashed border-border rounded-md">{text}</div>;
}

function ExploreCard({ item, accent, onOpen, testid }) {
  return (
    <button onClick={onOpen} data-testid={testid} className="w-full text-left rounded-md border border-border overflow-hidden hover-lift bg-card flex">
      {item.image_url && <img src={fileUrl(item.image_url)} alt={item.title} className="w-28 h-28 object-cover shrink-0" />}
      <div className="p-3 flex-1 min-w-0">
        <span className="text-[10px] uppercase tracking-widest" style={{ color: accent }}>{item.category}{item.distance ? ` · ${item.distance}` : ""}</span>
        <h3 className="font-serif text-lg mt-0.5 truncate">{item.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-muted-foreground self-center mr-2 shrink-0" />
    </button>
  );
}

function DetailView({ item, accent, onClose }) {
  const gallery = item.gallery || [];
  const sections = item.sections || [];
  return (
    <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.3 }}
      className="absolute inset-0 z-50 bg-background overflow-y-auto pb-10" data-testid="detail-view">
      <div className="relative h-60">
        <img src={fileUrl(item.image_url) || (gallery[0] && fileUrl(gallery[0]))} alt={item.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <button onClick={onClose} data-testid="detail-back" className="absolute top-4 left-4 bg-white/20 backdrop-blur-md rounded-full p-2.5 text-white"><ChevronLeft className="w-4 h-4" /></button>
        <div className="absolute bottom-4 left-5 right-5 text-white">
          <span className="text-[10px] uppercase tracking-widest" style={{ color: accent }}>{item.category}{item.distance ? ` · ${item.distance}` : ""}</span>
          <h1 className="font-serif text-3xl leading-tight">{item.title}</h1>
        </div>
      </div>
      <div className="p-5 space-y-6">
        {item.description && <p className="text-muted-foreground leading-relaxed">{item.description}</p>}
        {item.address && <p className="text-sm flex items-center gap-2 text-foreground"><MapPin className="w-4 h-4" style={{ color: accent }} />{item.address}</p>}
        {gallery.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {gallery.map((g, i) => <img key={i} src={fileUrl(g)} alt="" className="w-40 h-28 object-cover rounded-md shrink-0" data-testid={`detail-gallery-${i}`} />)}
          </div>
        )}
        {sections.map((s, i) => (
          <div key={i} data-testid={`detail-section-${i}`}>
            {s.heading && <h3 className="font-serif text-xl mb-1">{s.heading}</h3>}
            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">{s.body}</p>
          </div>
        ))}
        {item.link && <a href={item.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm" style={{ color: accent }}>Visit website <ChevronRight className="w-3 h-3" /></a>}
      </div>
    </motion.div>
  );
}

function ServiceRequestDialog({ service, onClose, slug, guest, accent }) {
  const [form, setForm] = useState({ guest_name: "", room: "", contact: "", date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (service) { setDone(false); setForm({ guest_name: guest?.name || "", room: guest?.room || "", contact: guest?.email || "", date: "", notes: "" }); }
  }, [service, guest]);

  const submit = async () => {
    if (!form.guest_name) return toast.error("Please enter your name");
    setLoading(true);
    try {
      await axios.post(`${API}/public/hub/${slug}/bookings`, { service_id: service.id, schedule_event_id: service.schedule_event_id || null, service_name: service.name, ...form }, authHeader(slug));
      setDone(true);
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not send request"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!service} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        {service && (done ? (
          <div className="text-center py-8" data-testid="request-success">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: accent }} />
            <h3 className="font-serif text-2xl">Request sent</h3>
            <p className="text-muted-foreground text-sm mt-2">The team has received your request for {service.name}.</p>
            <Button onClick={onClose} className="mt-6 bg-primary text-primary-foreground" data-testid="request-close">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-serif text-xl">Request {service.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Your name</Label><Input data-testid="request-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Room</Label><Input data-testid="request-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Preferred time</Label><Input data-testid="request-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" placeholder="e.g. 7pm" /></div>
              </div>
              <div><Label>Contact (optional)</Label><Input data-testid="request-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Notes</Label><Textarea data-testid="request-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" /></div>
              <Button onClick={submit} disabled={loading} data-testid="request-submit" className="w-full text-black" style={{ backgroundColor: accent }}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}</Button>
            </div>
          </>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function ComplaintDialog({ open, onClose, slug, guest, accent }) {
  const CATS = ["Housekeeping", "Maintenance", "Noise", "Room Amenities", "Food & Beverage", "General"];
  const [form, setForm] = useState({ category: "Housekeeping", room: "", guest_name: "", description: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setForm({ category: "Housekeeping", room: guest?.room || "", guest_name: guest?.name || "", description: "" }); }, [open, guest]);
  const submit = async () => {
    if (!form.description) return toast.error("Please describe the issue");
    setLoading(true);
    try { await axios.post(`${API}/public/hub/${slug}/complaints`, form, authHeader(slug)); toast.success("Reported — our team will look into it"); onClose(); }
    catch { toast.error("Could not submit"); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-xl">Report an issue</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Category</Label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="complaint-category" className="mt-1.5 w-full h-10 rounded-md border border-input px-3 text-sm bg-background">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input data-testid="complaint-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Room</Label><Input data-testid="complaint-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>Describe the issue</Label><Textarea data-testid="complaint-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5" /></div>
          <Button onClick={submit} disabled={loading} data-testid="complaint-submit" className="w-full text-black" style={{ backgroundColor: accent }}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit report"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SetPasswordDialog({ token, slug, accent, setGuest, onDone }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (password.length < 4) return toast.error("Password must be at least 4 characters");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/hub/${slug}/guest/set-password`, { token, password });
      localStorage.setItem(`grh_guest_token_${slug}`, data.token);
      localStorage.setItem(`grh_guest_${slug}`, JSON.stringify(data.user));
      setGuest(data.user);
      toast.success("Password set — you're signed in");
      onDone();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Invalid or expired link"); onDone(); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={!!token} onOpenChange={(o) => !o && onDone()}>
      <DialogContent className="max-w-sm" data-testid="setpw-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl flex items-center gap-2"><KeyRound className="w-5 h-5" style={{ color: accent }} /> Set your password</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Create a password to manage your bookings, requests and room service.</p>
        <div className="space-y-3 mt-2">
          <div><Label>New password</Label><Input data-testid="setpw-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" /></div>
          <Button onClick={submit} disabled={loading} data-testid="setpw-submit" className="w-full text-black" style={{ backgroundColor: accent }}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set password & sign in"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GuestAuthDialog({ open, onClose, slug, guest, setGuest, accent }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", room: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stay, setStay] = useState(null);

  useEffect(() => {
    if (open && guest) {
      setStay(null);
      Promise.all([
        axios.get(`${API}/guest/bookings`, authHeader(slug)).catch(() => ({ data: [] })),
        axios.get(`${API}/guest/room-orders`, authHeader(slug)).catch(() => ({ data: [] })),
        axios.get(`${API}/guest/folio`, authHeader(slug)).catch(() => ({ data: { orders: [], total: 0 } })),
        axios.get(`${API}/guest/upgrade-requests`, authHeader(slug)).catch(() => ({ data: [] })),
      ]).then(([b, o, f, u]) => setStay({ bookings: b.data, orders: o.data, folio: f.data, upgrades: u.data }));
    }
  }, [open, guest, slug]);

  const submit = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/public/hub/${slug}/guest/${mode === "login" ? "login" : "register"}`, form);
      localStorage.setItem(`grh_guest_token_${slug}`, data.token);
      localStorage.setItem(`grh_guest_${slug}`, JSON.stringify(data.user));
      setGuest(data.user);
      toast.success("Signed in");
    } catch (e) { const d = e.response?.data?.detail; setError(typeof d === "string" ? d : "Something went wrong"); }
    finally { setLoading(false); }
  };

  const signOut = () => {
    localStorage.removeItem(`grh_guest_token_${slug}`);
    localStorage.removeItem(`grh_guest_${slug}`);
    setGuest(null); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
        {guest ? (
          <div data-testid="guest-profile">
            <div className="text-center pb-4 border-b border-border">
              <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-black font-serif text-xl mb-3" style={{ backgroundColor: accent }}>{guest.name?.[0]?.toUpperCase()}</div>
              <h3 className="font-serif text-xl">{guest.name}</h3>
              <p className="text-muted-foreground text-sm">{guest.email}</p>
              {guest.room && <p className="text-muted-foreground text-sm">Room {guest.room}</p>}
              {(guest.check_in || guest.check_out) && <p className="text-xs text-muted-foreground mt-1">{guest.check_in || "—"} → {guest.check_out || "—"}</p>}
            </div>

            {stay === null ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-accent" /></div> : (
              <div className="py-4 space-y-5">
                <div className="rounded-md p-4 text-black flex items-center justify-between" style={{ backgroundColor: accent }} data-testid="folio-summary">
                  <span className="flex items-center gap-2 text-sm"><Receipt className="w-4 h-4" /> Room folio</span>
                  <span className="font-serif text-xl">£{(stay.folio.total || 0).toFixed(2)}</span>
                </div>

                <StaySection title="Room service" items={stay.orders} render={(o) => (
                  <div key={o.id} className="rounded-md border border-border p-3 flex items-center justify-between" data-testid={`my-order-${o.id}`}>
                    <div><p className="text-sm font-medium text-foreground">{(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}</p><p className="text-xs text-muted-foreground">£{o.total}</p></div>
                    <Badge className={`${statusColor[o.status]} border-0 capitalize`}>{o.status}</Badge>
                  </div>
                )} empty="No orders yet." />

                <StaySection title="My requests" items={stay.bookings} render={(r) => (
                  <div key={r.id} className="rounded-md border border-border p-3 flex items-center justify-between" data-testid={`guest-request-${r.id}`}>
                    <div><p className="text-sm font-medium text-foreground">{r.service_name}</p><p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p></div>
                    <Badge className={`${statusColor[r.status]} border-0 capitalize`}>{r.status}</Badge>
                  </div>
                )} empty="No requests yet." />

                <StaySection title="Stay upgrades" items={stay.upgrades} render={(u) => (
                  <div key={u.id} className="rounded-md border border-border p-3 flex items-center justify-between" data-testid={`my-upgrade-${u.id}`}>
                    <div><p className="text-sm font-medium text-foreground">{u.title}</p><p className="text-xs text-muted-foreground">£{Number(u.fee).toFixed(2)}</p></div>
                    <Badge className={`${statusColor[u.status] || "bg-secondary"} border-0 capitalize`}>{u.status}</Badge>
                  </div>
                )} empty="No upgrades requested." />
              </div>
            )}

            <FeedbackCard slug={slug} accent={accent} />

            <Button onClick={signOut} variant="outline" className="w-full" data-testid="guest-signout"><LogOut className="w-4 h-4 mr-2" /> Sign out</Button>
          </div>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-serif text-xl">{mode === "login" ? "Guest sign in" : "Create guest account"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {mode === "register" && (<>
                <div><Label>Name</Label><Input data-testid="guest-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Room</Label><Input data-testid="guest-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} className="mt-1.5" /></div>
              </>)}
              <div><Label>Email</Label><Input data-testid="guest-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Password</Label><Input data-testid="guest-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" /></div>
              {error && <p className="text-sm text-red-500" data-testid="guest-auth-error">{error}</p>}
              <Button onClick={submit} disabled={loading} data-testid="guest-auth-submit" className="w-full text-black" style={{ backgroundColor: accent }}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "login" ? <><LogIn className="w-4 h-4 mr-2" />Sign in</> : "Create account")}</Button>
              <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} data-testid="guest-auth-switch" className="text-sm text-muted-foreground w-full text-center pt-1">{mode === "login" ? "New guest? Create an account" : "Already registered? Sign in"}</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StaySection({ title, items, render, empty }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</p>
      {items && items.length ? <div className="space-y-2">{items.map(render)}</div> : <p className="text-sm text-muted-foreground">{empty}</p>}
    </div>
  );
}

function FeedbackCard({ slug, accent }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/hub/${slug}/feedback`, { rating, comment }, authHeader(slug));
      setLinks(data.review_links && Object.keys(data.review_links).length ? data.review_links : null);
      setSent(true); toast.success("Thanks for your feedback!");
    } catch { toast.error("Could not submit feedback"); }
    finally { setBusy(false); }
  };
  const openReview = (platform, url) => {
    axios.post(`${API}/public/hub/${slug}/review-click`, { platform }).catch(() => {});
    window.open(url, "_blank", "noopener");
  };
  if (sent) return (
    <div className="rounded-md border border-border p-4 text-center mb-3" data-testid="feedback-thanks">
      <CheckCircle2 className="w-6 h-6 mx-auto mb-1" style={{ color: accent }} />
      <p className="text-sm text-foreground">Thank you for your rating.</p>
      {links && (
        <div className="mt-3 space-y-2" data-testid="review-links">
          <p className="text-xs text-muted-foreground">Would you share it with other travellers? It really helps us.</p>
          {Object.entries(links).map(([p, url]) => (
            <Button key={p} onClick={() => openReview(p, url)} className="w-full text-black" style={{ backgroundColor: accent }} data-testid={`review-link-${p}`}>
              <ExternalLink className="w-4 h-4 mr-2" /> Review us on {p === "google" ? "Google" : "TripAdvisor"}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="rounded-md border border-border p-4 mb-3" data-testid="feedback-card">
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Rate your stay</p>
      <div className="flex gap-1 mb-3" data-testid="feedback-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} data-testid={`feedback-star-${n}`}>
            <Star className="w-7 h-7 transition-colors" style={{ color: (hover || rating) >= n ? accent : "#d4d4d8" }} fill={(hover || rating) >= n ? accent : "none"} />
          </button>
        ))}
      </div>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tell us how it's going (optional)" className="mb-3" data-testid="feedback-comment" />
      <Button onClick={submit} disabled={busy} className="w-full text-black" style={{ backgroundColor: accent }} data-testid="feedback-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit feedback"}</Button>
    </div>
  );
}

function ConciergeChat({ open, onClose, slug, guest, accent, aiEnabled, staffEnabled }) {
  const [mode, setMode] = useState(aiEnabled ? "ai" : "staff");
  const [threadId, setThreadId] = useState(() => localStorage.getItem(`grh_thread_${slug}`) || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState(guest?.name || "");
  const [room, setRoom] = useState(guest?.room || "");
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const bottomRef = useRef(null);

  const load = () => { if (!threadId) return; axios.get(`${API}/public/hub/${slug}/messages`, { params: { thread_id: threadId } }).then((r) => setMessages(r.data)).catch(() => {}); };
  useEffect(() => { if (!open || mode !== "staff") return; load(); const t = setInterval(load, 4000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [open, threadId, mode]);
  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, aiMessages, open]);

  const send = async () => {
    if (!text.trim()) return;
    if (!name.trim()) return toast.error("Please enter your name first");
    try {
      const { data } = await axios.post(`${API}/public/hub/${slug}/messages`, { thread_id: threadId, guest_name: name, room, text: text.trim() }, authHeader(slug));
      if (!threadId) { setThreadId(data.thread_id); localStorage.setItem(`grh_thread_${slug}`, data.thread_id); }
      setText(""); setTimeout(load, 200);
    } catch { toast.error("Could not send message"); }
  };

  const askAi = async () => {
    const q = text.trim();
    if (!q || aiLoading) return;
    setAiMessages((m) => [...m, { role: "user", text: q }]);
    setText(""); setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/hub/${slug}/ai-concierge`, { question: q });
      setAiMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) {
      setAiMessages((m) => [...m, { role: "ai", text: e.response?.data?.detail || "Sorry, I'm unavailable right now — please message reception." }]);
    } finally { setAiLoading(false); }
  };

  const onSubmit = () => (mode === "ai" ? askAi() : send());

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0">
        <div className="p-4 text-black" style={{ backgroundColor: accent }}>
          <DialogHeader><DialogTitle className="text-black font-serif text-xl">Concierge</DialogTitle></DialogHeader>
          {aiEnabled && staffEnabled && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => setMode("ai")} data-testid="chat-mode-ai" className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${mode === "ai" ? "bg-black text-white" : "bg-black/10 text-black"}`}><Sparkles className="w-3.5 h-3.5" /> Ask AI</button>
              <button onClick={() => setMode("staff")} data-testid="chat-mode-staff" className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${mode === "staff" ? "bg-black text-white" : "bg-black/10 text-black"}`}><MessageCircle className="w-3.5 h-3.5" /> Message reception</button>
            </div>
          )}
          <p className="text-black/70 text-xs mt-2">{mode === "ai" ? "Instant answers about your stay, powered by AI." : "Message reception — we usually reply within minutes."}</p>
        </div>

        {mode === "staff" && !guest && (
          <div className="px-4 pt-3 grid grid-cols-2 gap-2">
            <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="chat-name" />
            <Input placeholder="Room (optional)" value={room} onChange={(e) => setRoom(e.target.value)} data-testid="chat-room" />
          </div>
        )}

        <div className="h-72 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {mode === "ai" ? (
            aiMessages.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">
                <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: accent }} />
                <p>Ask me anything about your stay —<br />wifi, check-out, dining or local tips.</p>
              </div>
            ) : aiMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "text-black" : "bg-secondary text-foreground"}`} style={m.role === "user" ? { backgroundColor: accent } : {}}><p className="whitespace-pre-wrap">{m.text}</p></div>
              </div>
            ))
          ) : (
            messages.length === 0 ? <p className="text-center text-sm text-muted-foreground py-10">Send a message to start the conversation.</p> : messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "guest" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === "guest" ? "text-black" : "bg-secondary text-foreground"}`} style={m.sender === "guest" ? { backgroundColor: accent } : {}}><p>{m.text}</p></div>
              </div>
            ))
          )}
          {aiLoading && mode === "ai" && <div className="flex justify-start"><div className="bg-secondary rounded-lg px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-accent" /></div></div>}
          <div ref={bottomRef} />
        </div>

        <div className="p-3 border-t border-border flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSubmit()} placeholder={mode === "ai" ? "Ask the AI concierge…" : "Type a message…"} data-testid="chat-input" />
          <Button onClick={onSubmit} disabled={aiLoading && mode === "ai"} data-testid="chat-send-btn" className="text-black" style={{ backgroundColor: accent }}>{aiLoading && mode === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

